import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Eye,
  EyeOff,
  GripVertical,
  Pencil,
  Plus,
  RotateCcw,
  Trash2, // <--- Imported Trash icon
} from "lucide-react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  SCREEN_DEFINITIONS,
  getSectionDefinition,
  getSupportedContentTypes,
} from "./homepageConfig";

import {
  useCreateScreenSectionsMutation,
  useLazyGetScreenSectionsQuery,
  useReorderScreenSectionsMutation,
  useLazyGetBannerDashboardQuery,
  useUpdateSectionStatusMutation,
  useDeleteScreenSectionMutation, // <--- Imported Delete Mutation
} from "../../../redux/actions/Admin/ProductAdmin";

import { useAlert } from "../../../components/customer/AlertContext"; // <--- Imported Alert Context

type Section = {
  id: number;
  sectionType: string;
  sectionName: string;

  contentType?: string | null;

  displayName?: string;
  description?: string | null;
  layoutType?: string | null;
  maxItems?: number;
  allowMultiple?: boolean;
  recommendedWidth?: number;
  recommendedHeight?: number;
  aspectRatio?: string | null;
  mobileRecommendedWidth?: number | null;
  mobileRecommendedHeight?: number | null;
  mobileAspectRatio?: string | null;
  isVisible: boolean;
  clientType?: string;
  configJson?: string | null;
  displayOrder: number;
};

const HomeSectionsScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showConfirm } = useAlert(); // <--- Initialized Alert Hook

  const state = location.state as
    | {
        screenId?: number;
        screenName?: string;
      }
    | undefined;

  // ============================================================
  // SCREEN
  // ============================================================

  const [selectedScreenId, setSelectedScreenId] = useState<number>(
    state?.screenId ?? 1,
  );

  const [sections, setSections] = useState<Section[]>([]);

  const [updateSectionStatus, { isLoading: isUpdatingStatus }] =
  useUpdateSectionStatusMutation();

  // ============================================================
  // DELETE MUTATION
  // ============================================================
  const [deleteScreenSection, { isLoading: isDeleting }] =
    useDeleteScreenSectionMutation();

  // ============================================================
  // DRAG STATE
  // ============================================================

  const [draggedSectionId, setDraggedSectionId] = useState<number | null>(null);

  const [dragOverSectionId, setDragOverSectionId] = useState<number | null>(
    null,
  );

  // ============================================================
  // ORDER EDIT STATE
  // ============================================================

  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);

  const [orderValue, setOrderValue] = useState<string>("");

  // ============================================================
  // API
  // ============================================================

  const [
    getSections,
    { data: sectionsResponse, isLoading, isFetching, isError },
  ] = useLazyGetScreenSectionsQuery();

  const [createSection, { isLoading: isCreating }] =
    useCreateScreenSectionsMutation();

  const [reorderSections, { isLoading: isReordering }] =
    useReorderScreenSectionsMutation();

  const [getBannerDashboard, { isFetching: isFetchingBannerDashboard }] =
    useLazyGetBannerDashboardQuery();

  // ============================================================
  // SELECTED SCREEN
  // ============================================================

  const selectedScreen = useMemo(
    () => SCREEN_DEFINITIONS.find((screen) => screen.id === selectedScreenId),
    [selectedScreenId],
  );

  // ============================================================
  // LOAD SECTIONS
  // ============================================================

  const loadSections = useCallback(() => {
    getSections(selectedScreenId);
  }, [getSections, selectedScreenId]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  // ============================================================
  // SET SECTIONS
  // ============================================================

  useEffect(() => {
    if (!sectionsResponse) {
      return;
    }

    setSections(
      [...sectionsResponse].sort(
        (a: Section, b: Section) => a.displayOrder - b.displayOrder,
      ),
    );
  }, [sectionsResponse]);

  // ============================================================
  // PERSIST ORDER
  // ============================================================

  const persistSectionOrder = async (
    reorderedSections: Section[],
    previousSections: Section[],
  ) => {
    const normalized = reorderedSections.map((section, index) => ({
      ...section,

      displayOrder: index + 1,
    }));

    try {
      await reorderSections({
        screenId: selectedScreenId,

        data: {
          sectionIds: normalized.map((section) => section.id),
        },
      }).unwrap();

      setSections(normalized);
    } catch (error) {
      console.error("Failed to reorder sections", error);

      setSections(previousSections);
    }
  };

  // ============================================================
  // DRAG START
  // ============================================================

  const handleDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    sectionId: number,
  ) => {
    setDraggedSectionId(sectionId);

    event.dataTransfer.effectAllowed = "move";

    event.dataTransfer.setData("text/plain", String(sectionId));
  };

  // ============================================================
  // DRAG OVER
  // ============================================================

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    sectionId: number,
  ) => {
    event.preventDefault();

    if (draggedSectionId === null) {
      return;
    }

    if (draggedSectionId === sectionId) {
      return;
    }

    event.dataTransfer.dropEffect = "move";

    setDragOverSectionId(sectionId);
  };

  // ============================================================
  // DROP
  // ============================================================

  const handleDrop = async (
    event: React.DragEvent<HTMLDivElement>,
    targetSectionId: number,
  ) => {
    event.preventDefault();

    const sourceSectionId = draggedSectionId;

    setDragOverSectionId(null);

    setDraggedSectionId(null);

    if (sourceSectionId === null || sourceSectionId === targetSectionId) {
      return;
    }

    const previousSections = [...sections];

    const sourceIndex = sections.findIndex(
      (section) => section.id === sourceSectionId,
    );

    const targetIndex = sections.findIndex(
      (section) => section.id === targetSectionId,
    );

    if (sourceIndex === -1 || targetIndex === -1) {
      return;
    }

    const reordered = [...sections];

    const [movedSection] = reordered.splice(sourceIndex, 1);

    reordered.splice(targetIndex, 0, movedSection);

    await persistSectionOrder(reordered, previousSections);
  };

  // ============================================================
  // DRAG END
  // ============================================================

  const handleDragEnd = () => {
    setDraggedSectionId(null);

    setDragOverSectionId(null);
  };

  // ============================================================
  // START ORDER EDIT
  // ============================================================

  const startOrderEdit = (section: Section) => {
    setEditingOrderId(section.id);

    setOrderValue(String(section.displayOrder));
  };

  // ============================================================
  // CANCEL ORDER EDIT
  // ============================================================

  const cancelOrderEdit = () => {
    setEditingOrderId(null);

    setOrderValue("");
  };

  // ============================================================
  // APPLY MANUAL ORDER
  // ============================================================

  const applyManualOrder = async (section: Section) => {
    const requestedOrder = Number(orderValue);

    if (!Number.isInteger(requestedOrder)) {
      cancelOrderEdit();

      return;
    }

    if (requestedOrder < 1 || requestedOrder > sections.length) {
      cancelOrderEdit();

      return;
    }

    if (requestedOrder === section.displayOrder) {
      cancelOrderEdit();

      return;
    }

    const previousSections = [...sections];

    const currentIndex = sections.findIndex((item) => item.id === section.id);

    if (currentIndex === -1) {
      cancelOrderEdit();

      return;
    }

    const reordered = [...sections];

    const [movedSection] = reordered.splice(currentIndex, 1);

    reordered.splice(requestedOrder - 1, 0, movedSection);

    cancelOrderEdit();

    await persistSectionOrder(reordered, previousSections);
  };

  // ============================================================
  // ORDER KEY HANDLER
  // ============================================================

  const handleOrderKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    section: Section,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();

      applyManualOrder(section);

      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();

      cancelOrderEdit();
    }
  };

  // ============================================================
  // CREATE
  // ============================================================

  const openCreate = () => {
    navigate("/admin/uiconfigure/sections/new", {
      state: {
        screenId: selectedScreenId,

        screenName: selectedScreen?.name,
      },
    });
  };

  // ============================================================
  // EDIT
  // ============================================================

  const openEdit = (section: Section) => {
    navigate(`/admin/uiconfigure/sections/${section.id}/edit`, {
      state: {
        screenId: selectedScreenId,

        screenName: selectedScreen?.name,

        section,
      },
    });
  };

  // ============================================================
  // DELETE SECTION WITH ALERT
  // ============================================================

  const handleDeleteSection = (section: Section) => {
    showConfirm({
      title: "Delete Section",
      message: `Are you sure you want to delete "${section.displayName || section.sectionName || section.sectionType}"? This action cannot be undone.`,
      type: "warning",
      confirmText: "Delete",
      showCancel: true,
      onConfirm: async () => {
        try {
          await deleteScreenSection(section.id).unwrap();
          loadSections(); // Refresh sections after deletion
        } catch (error) {
          console.error("Failed to delete section", error);
        }
      },
    });
  };

  // ============================================================
  // MANAGE CONTENT / ITEMS
  // ============================================================

  const BANNER_SECTION_TYPES = [
    "MainCarousel",
    "StripBanner",
    "PetPromoBanner",
    "PetBanner",
    "PromoBanner",
    "WellnessBanner",
    "ConsultationBanner",
    "MultiBanner",
  ];

  const openItems = async (section: Section) => {
    const isBannerSection = BANNER_SECTION_TYPES.includes(section.sectionType);

    if (isBannerSection) {
      try {
        const dashboardData =
          await getBannerDashboard(selectedScreenId).unwrap();

        const bannerSection = dashboardData?.sections?.find(
          (item: any) => Number(item.sectionId) === Number(section.id),
        );

        navigate("/admin/offer/adBanner/adBannerInnerSection", {
          state: {
            screenId: selectedScreenId,

            sectionId: section.id,

            sectionData: bannerSection || {
              ...section,
              banners: [],
            },
          },
        });
      } catch (error) {
        console.error("Failed to load banner dashboard", error);
      }

      return;
    }

    const contentType =
      section.contentType ||
      getSectionDefinition(section.sectionType)?.itemType ||
      "Custom";

    navigate(`/admin/uiconfigure/sections/${section.id}/items`, {
      state: {
        section,

        screenId: selectedScreenId,

        screenName: selectedScreen?.name,

        contentType,
      },
    });
  };

  // ============================================================
  // PREVIEW
  // ============================================================

  const openPreview = (section: Section) => {
    navigate(`/admin/uiconfigure/sections/${section.id}/preview`, {
      state: {
        section,

        screenId: selectedScreenId,

        screenName: selectedScreen?.name,
      },
    });
  };

  // ============================================================
  // ACTIVE / INACTIVE
  // ============================================================

  const toggleVisibility = async (section: Section) => {
    const nextVisibleState = !section.isVisible;
    const previousSections = [...sections];

    setSections((current) =>
      current.map((item) =>
        item.id === section.id
          ? {
              ...item,
              isVisible: nextVisibleState,
            }
          : item,
      ),
    );

    try {
      await updateSectionStatus({
        sectionId: section.id,
        data: {
          isActive: nextVisibleState,
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to update section status", error);
      setSections(previousSections);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="w-full px-4 pb-12">
      {/* SCREEN TABS */}
      <div className="border-b border-slate-200 mb-8">
        <div className="flex gap-8">
          {SCREEN_DEFINITIONS.map((screen) => {
            const active = selectedScreenId === screen.id;

            return (
              <button
                key={screen.id}
                type="button"
                onClick={() => setSelectedScreenId(screen.id)}
                className={`px-1 py-4 text-sm font-medium border-b-2 transition ${
                  active
                    ? "border-black text-black"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {screen.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* HEADER */}
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">
            {selectedScreen?.name} Screen
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            Manage sections, content and display order.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-black text-white rounded-xl px-5 py-3 text-sm font-medium hover:bg-slate-800 transition"
        >
          <Plus size={17} />
          Add Section
        </button>
      </div>

      {/* STATUS */}
      {(isLoading ||
        isFetching ||
        isCreating ||
        isReordering ||
        isUpdatingStatus ||
        isDeleting ||
        isFetchingBannerDashboard) && (
        <div className="mb-4 flex items-center gap-2 text-xs text-slate-400">
          <RotateCcw size={13} className="animate-spin" />
          Updating configuration...
        </div>
      )}

      {/* ERROR */}
      {isError && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
          Failed to load sections.
        </div>
      )}

      {/* SECTION LIST */}
      <div className="space-y-3">
        {sections.map((section) => {
          const definition = getSectionDefinition(
            section.sectionType,
            selectedScreen?.name,
          );

          const supportedContentTypes = getSupportedContentTypes(
            section.sectionType,
            selectedScreen?.name,
          );
          const isConfigurable = definition?.configurable === true;

          const managerLabel =
            definition?.managerType === "Banner"
              ? "Manage Banners"
              : "Manage Content";
          const isDragging = draggedSectionId === section.id;
          const isDragTarget = dragOverSectionId === section.id;

          return (
            <div
              key={section.id}
              draggable
              onDragStart={(event) => handleDragStart(event, section.id)}
              onDragOver={(event) => handleDragOver(event, section.id)}
              onDrop={(event) => handleDrop(event, section.id)}
              onDragEnd={handleDragEnd}
              className={`
                  group
                  border
                  rounded-2xl
                  bg-white
                  transition-all
                  duration-150
                  ${
                    isDragging
                      ? "opacity-50 scale-[0.995] border-slate-400"
                      : "border-slate-200 hover:border-slate-300"
                  }
                  ${isDragTarget ? "border-black ring-2 ring-slate-100" : ""}
                `}
            >
              <div className="px-5 py-4">
                <div className="flex items-center gap-4">
                  {/* DRAG HANDLE */}
                  <div
                    title="Drag to reorder"
                    className="shrink-0 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500"
                  >
                    <GripVertical size={20} />
                  </div>

                  {/* ORDER */}
                  <div className="shrink-0">
                    {editingOrderId === section.id ? (
                      <input
                        autoFocus
                        type="number"
                        min={1}
                        max={sections.length}
                        value={orderValue}
                        onChange={(event) => setOrderValue(event.target.value)}
                        onBlur={() => applyManualOrder(section)}
                        onKeyDown={(event) =>
                          handleOrderKeyDown(event, section)
                        }
                        className="w-12 h-9 text-center text-sm font-semibold border border-slate-300 rounded-lg outline-none focus:border-black"
                      />
                    ) : (
                      <button
                        type="button"
                        title="Click to edit display order"
                        onClick={() => startOrderEdit(section)}
                        className="w-12 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700 transition"
                      >
                        {String(section.displayOrder).padStart(2, "0")}
                      </button>
                    )}
                  </div>

                  {/* SECTION INFO */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-950 truncate">
                        {section.displayName?.trim() ||
                          section.sectionName?.trim() ||
                          section.sectionType}
                      </h3>

                      {section.sectionName?.trim() && (
                        <span className="inline-flex items-center rounded-md bg-slate-50 border border-slate-200 px-2 py-1 text-[10px] font-medium text-slate-500">
                          {section.sectionName}
                        </span>
                      )}

                      <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-500">
                        {section.sectionType}
                      </span>

                      {supportedContentTypes.includes("Custom") && (
                        <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700">
                          Custom Content
                        </span>
                      )}

                      {!section.isVisible && (
                        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-500">
                          Inactive
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                      {definition?.itemType && (
                        <span>{definition.itemType}</span>
                      )}

                      <span>Client: {section.clientType || "All"}</span>

                      <span>Order: {section.displayOrder}</span>

                      {section.layoutType && (
                        <span>Layout: {section.layoutType}</span>
                      )}
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* MANAGE */}
                    {isConfigurable && (
                      <button
                        type="button"
                        onClick={() => openItems(section)}
                        className="border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg px-3 py-2 text-xs font-medium text-slate-700 transition"
                      >
                        {managerLabel}
                      </button>
                    )}

                    {/* PREVIEW */}
                    <button
                      type="button"
                      title="Preview section"
                      onClick={() => openPreview(section)}
                      className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
                    >
                      <Eye size={17} />
                    </button>

                    {/* EDIT */}
                    <button
                      type="button"
                      title="Edit section"
                      onClick={() => openEdit(section)}
                      className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
                    >
                      <Pencil size={17} />
                    </button>

                    {/* DELETE */}
                    <button
                      type="button"
                      title="Delete section"
                      onClick={() => handleDeleteSection(section)}
                      className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition"
                    >
                      <Trash2 size={17} />
                    </button>

                    {/* ACTIVE / INACTIVE */}
                    <button
                      type="button"
                      title={
                        section.isVisible
                          ? "Deactivate section"
                          : "Activate section"
                      }
                      onClick={() => toggleVisibility(section)}
                      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
                        section.isVisible ? "bg-emerald-500" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition ${
                          section.isVisible ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>

                    <span
                      className={`text-[10px] font-medium w-14 ${
                        section.isVisible
                          ? "text-emerald-600"
                          : "text-slate-400"
                      }`}
                    >
                      {section.isVisible ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              {/* DROP INDICATOR */}
              {isDragTarget && <div className="h-0.5 bg-black mx-5" />}
            </div>
          );
        })}

        {/* EMPTY */}
        {!isLoading && sections.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <p className="text-sm font-medium text-slate-600">
              No sections configured.
            </p>

            <p className="text-xs text-slate-400 mt-1">
              Click Add Section to create your first section.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeSectionsScreen;